import type {
  BookedProducts,
  BookedService,
  BookedServiceWorkSchedule,
  Employee,
  Order,
  ServiceVariant,
} from '@/libs/interfaces'
import { OrderStatus } from '@/libs/interfaces'

import { mockProducts } from './products'
import {
  MOCK_ORG_ID,
  MOCK_STORE_ID_1,
  MOCK_STORE_ID_2,
  mockCustomers,
  mockEmployees,
  mockVehicles,
} from './seed'
import { mockServices } from './services'

const buildWorkSchedule = (
  variant: ServiceVariant | undefined,
  employee: Employee,
  workDay: string,
  serviceIndex: number,
  variantIndex: number,
): BookedServiceWorkSchedule => ({
  employeeID: employee.employeeID ?? '',
  workDay,
  workDuration: variant?.serviceVariantDays?.[0]?.duration ?? 60,
  workDayID: `work-day-${serviceIndex}-${variantIndex}`,
})

const buildBookedService = (
  serviceIndex: number,
  variantIndex: number,
  employeeIndex: number,
  workDay: string,
): BookedService => {
  const service = mockServices[serviceIndex]
  const variant = service.serviceVariants?.[variantIndex]
  const employee = mockEmployees[employeeIndex]
  const cost = variant?.cost ?? service.cost ?? 0

  return {
    service: {
      serviceID: service.serviceID ?? '',
      serviceVariantID: variant?.serviceVariantID ?? '',
      name: service.name ?? '',
      amount: 1,
      cost,
      currency: 'SEK',
      vatFree: false,
      orderNotes: '',
      kanbanStatus: 'TODO',
      total: cost,
    },
    days: [buildWorkSchedule(variant, employee, workDay, serviceIndex, variantIndex)],
  }
}

const buildBookedProduct = (productIndex: number, amount: string): BookedProducts => {
  const product = mockProducts[productIndex]
  const cost = product?.cost ?? 0

  return {
    productID: product?.productID ?? '',
    productDescription: product?.productDescription ?? '',
    amount,
    cost: String(cost),
    currency: 'SEK',
    orderProductNotes: '',
    total: cost * Number(amount),
  }
}

export const mockBookings: Order[] = [
  {
    orderID: 'order-1',
    orderNumber: 1001,
    driver: mockCustomers[0],
    driverCar: mockVehicles[0],
    submissionTime: '2025-06-01T08:00:00Z',
    pickupTime: '2025-06-01T16:00:00Z',
    updatedAt: '2025-06-01T08:00:00Z',
    orderStatus: OrderStatus.preliminary,
    total: [600],
    currency: ['SEK'],
    billed: false,
    services: [buildBookedService(0, 0, 0, '2025-06-01')],
    products: [],
    orgID: MOCK_ORG_ID,
    storeID: MOCK_STORE_ID_1,
  },
  {
    orderID: 'order-2',
    orderNumber: 1002,
    driver: mockCustomers[1],
    driverCar: mockVehicles[1],
    submissionTime: '2025-06-02T08:00:00Z',
    pickupTime: '2025-06-02T16:00:00Z',
    updatedAt: '2025-06-02T08:00:00Z',
    orderStatus: OrderStatus.created,
    total: [300, 80],
    currency: ['SEK', 'SEK'],
    billed: false,
    services: [buildBookedService(1, 0, 1, '2025-06-02')],
    products: [buildBookedProduct(1, '1')],
    orgID: MOCK_ORG_ID,
    storeID: MOCK_STORE_ID_1,
  },
  {
    orderID: 'order-3',
    orderNumber: 1003,
    driver: mockCustomers[2],
    driverCar: mockVehicles[2],
    submissionTime: '2025-06-03T08:00:00Z',
    submittedTime: '2025-06-03T08:05:00Z',
    pickupTime: '2025-06-03T16:00:00Z',
    updatedAt: '2025-06-03T08:05:00Z',
    orderStatus: OrderStatus.inShop,
    total: [1200, 220],
    currency: ['SEK', 'SEK'],
    billed: false,
    services: [buildBookedService(2, 0, 2, '2025-06-03')],
    products: [buildBookedProduct(3, '1')],
    orgID: MOCK_ORG_ID,
    storeID: MOCK_STORE_ID_1,
  },
  {
    orderID: 'order-4',
    orderNumber: 1004,
    driver: mockCustomers[3],
    driverCar: mockVehicles[3],
    submissionTime: '2025-06-04T08:00:00Z',
    submittedTime: '2025-06-04T08:05:00Z',
    pickupTime: '2025-06-04T16:00:00Z',
    updatedAt: '2025-06-04T09:00:00Z',
    orderStatus: OrderStatus.startService,
    total: [450],
    currency: ['SEK'],
    billed: false,
    services: [buildBookedService(4, 0, 3, '2025-06-04')],
    products: [],
    orgID: MOCK_ORG_ID,
    storeID: MOCK_STORE_ID_1,
  },
  {
    orderID: 'order-5',
    orderNumber: 1005,
    driver: mockCustomers[4],
    driverCar: mockVehicles[4],
    submissionTime: '2025-05-28T08:00:00Z',
    submittedTime: '2025-05-28T08:05:00Z',
    pickupTime: '2025-05-28T16:00:00Z',
    pickedUpTime: '2025-05-28T15:40:00Z',
    updatedAt: '2025-05-28T15:40:00Z',
    orderStatus: OrderStatus.pickedUp,
    total: [800, 350],
    currency: ['SEK', 'SEK'],
    billed: true,
    services: [buildBookedService(8, 0, 4, '2025-05-28')],
    products: [buildBookedProduct(0, '1')],
    orgID: MOCK_ORG_ID,
    storeID: MOCK_STORE_ID_1,
  },
  {
    orderID: 'order-6',
    orderNumber: 1006,
    driver: mockCustomers[5],
    driverCar: mockVehicles[5],
    submissionTime: '2025-05-20T08:00:00Z',
    submittedTime: '2025-05-20T08:05:00Z',
    pickupTime: '2025-05-20T16:00:00Z',
    pickedUpTime: '2025-05-20T15:50:00Z',
    updatedAt: '2025-05-20T15:50:00Z',
    orderStatus: OrderStatus.finished,
    total: [1500],
    currency: ['SEK'],
    billed: true,
    services: [buildBookedService(9, 0, 0, '2025-05-20')],
    products: [],
    orgID: MOCK_ORG_ID,
    storeID: MOCK_STORE_ID_1,
  },
  {
    orderID: 'order-7',
    orderNumber: 1007,
    driver: mockCustomers[6],
    driverCar: mockVehicles[6],
    submissionTime: '2025-06-05T08:00:00Z',
    pickupTime: '2025-06-05T16:00:00Z',
    updatedAt: '2025-06-05T08:00:00Z',
    orderStatus: OrderStatus.created,
    total: [900],
    currency: ['SEK'],
    billed: false,
    services: [buildBookedService(5, 0, 1, '2025-06-05')],
    products: [],
    orgID: MOCK_ORG_ID,
    storeID: MOCK_STORE_ID_2,
  },
  {
    orderID: 'order-8',
    orderNumber: 1008,
    driver: mockCustomers[7],
    driverCar: mockVehicles[7],
    submissionTime: '2025-06-06T08:00:00Z',
    submittedTime: '2025-06-06T08:05:00Z',
    pickupTime: '2025-06-06T16:00:00Z',
    updatedAt: '2025-06-06T08:05:00Z',
    orderStatus: OrderStatus.inShop,
    total: [500, 950],
    currency: ['SEK', 'SEK'],
    billed: false,
    services: [buildBookedService(6, 0, 2, '2025-06-06')],
    products: [buildBookedProduct(5, '1')],
    orgID: MOCK_ORG_ID,
    storeID: MOCK_STORE_ID_2,
  },
]
